import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

const STATUS_META = {
  CONCLUIDO: { label: 'Concluído', color: '#0d9488', bg: 'rgba(13,148,136,0.12)' },
  EM_ANDAMENTO: { label: 'Em andamento', color: '#ea580c', bg: 'rgba(234,88,12,0.12)' },
  EM_ANALISE: { label: 'Em análise', color: '#0284c7', bg: 'rgba(2,132,199,0.12)' },
  PENDENTE: { label: 'Pendente', color: '#94a3b8', bg: 'rgba(148,163,184,0.14)' },
  REJEITADO: { label: 'Rejeitado', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
  CANCELADO: { label: 'Cancelado', color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
};

function formatDt(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function NodeIcon({ status, ehAtual }) {
  if (ehAtual || status === 'EM_ANDAMENTO' || status === 'EM_ANALISE') {
    return <RadioButtonCheckedRoundedIcon sx={{ fontSize: 22, color: '#ea580c' }} />;
  }
  if (status === 'CONCLUIDO') {
    return <CheckCircleRoundedIcon sx={{ fontSize: 22, color: '#0d9488' }} />;
  }
  return <HourglassEmptyRoundedIcon sx={{ fontSize: 20, color: '#94a3b8' }} />;
}

/**
 * Timeline visual do histórico de etapas do processo (análise + ludicidade).
 */
export default function ProcessoHistoricoTimeline({
  timeline = [],
  tempoTotalDias,
  tempoTotalHoras,
  dense = false,
  onEtapaClick,
  onArquivoClick,
  showRadar = true,
}) {
  const [openEvents, setOpenEvents] = useState({});

  const progresso = useMemo(() => {
    if (!timeline.length) return 0;
    const atualIdx = timeline.findIndex((e) => e.ehAtual);
    const base = atualIdx >= 0 ? atualIdx : timeline.filter((e) => e.status === 'CONCLUIDO').length;
    return Math.min(100, Math.round(((base + (timeline.some((e) => e.ehAtual) ? 0.45 : 0)) / timeline.length) * 100));
  }, [timeline]);

  const etapaAtual = timeline.find((e) => e.ehAtual);

  if (!timeline?.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        Nenhum histórico encontrado.
      </Typography>
    );
  }

  return (
    <Box>
      {showRadar && (
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: 3,
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(30,58,138,0.88) 55%, rgba(13,148,136,0.75) 100%)',
          color: '#e2e8f0',
          boxShadow: '0 12px 32px rgba(15,23,42,0.28)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 85% 20%, rgba(45,212,191,0.25), transparent 45%), radial-gradient(circle at 10% 80%, rgba(56,189,248,0.18), transparent 40%)',
            pointerEvents: 'none',
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ position: 'relative', mb: 1 }}>
          <BoltRoundedIcon sx={{ color: '#5eead4' }} />
          <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.4 }}>
            Radar do fluxo
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ position: 'relative', opacity: 0.9, mb: 1.5 }}>
          {etapaAtual
            ? `Etapa ativa: ${etapaAtual.nome}`
            : 'Fluxo sem etapa marcada como atual'}
          {(tempoTotalDias != null || tempoTotalHoras != null) &&
            ` · Tempo total: ${tempoTotalDias != null ? `${tempoTotalDias}d` : ''} ${
              tempoTotalHoras != null ? `${tempoTotalHoras}h` : ''
            }`}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#99f6e4', fontWeight: 700 }}>
              Progresso
            </Typography>
            <Typography variant="caption" sx={{ color: '#e0f2fe', fontWeight: 700 }}>
              {progresso}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progresso}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.12)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                background: 'linear-gradient(90deg, #2dd4bf, #38bdf8)',
              },
            }}
          />
        </Box>
      </Box>
      )}

      <Box sx={{ position: 'relative', pl: 0.5 }}>
        {timeline.map((etapa, index) => {
          const meta = STATUS_META[etapa.status] || STATUS_META.PENDENTE;
          const isLast = index === timeline.length - 1;
          const eventsOpen = !!openEvents[etapa.id];
          const eventos = Array.isArray(etapa.eventos) ? etapa.eventos : [];
          const arquivos = Array.isArray(etapa.arquivos) ? etapa.arquivos : [];
          const clickable = typeof onEtapaClick === 'function';

          return (
            <Box
              key={etapa.id || index}
              sx={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr',
                gap: 1.25,
                mb: isLast ? 0 : 0.5,
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: etapa.ehAtual ? 'rgba(234,88,12,0.15)' : 'rgba(15,23,42,0.04)',
                    border: etapa.ehAtual ? '2px solid #ea580c' : '2px solid transparent',
                    boxShadow: etapa.ehAtual ? '0 0 0 4px rgba(234,88,12,0.18)' : 'none',
                    zIndex: 1,
                    transition: 'box-shadow 0.25s ease',
                  }}
                >
                  <NodeIcon status={etapa.status} ehAtual={etapa.ehAtual} />
                </Box>
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      width: 3,
                      minHeight: dense ? 28 : 40,
                      mt: 0.5,
                      borderRadius: 2,
                      background: etapa.status === 'CONCLUIDO'
                        ? 'linear-gradient(180deg, #2dd4bf, #38bdf8)'
                        : 'linear-gradient(180deg, rgba(148,163,184,0.55), rgba(148,163,184,0.15))',
                    }}
                  />
                )}
              </Box>

              <Box
                onClick={clickable ? () => onEtapaClick(etapa) : undefined}
                sx={{
                  mb: 1.5,
                  p: dense ? 1.25 : 1.75,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: etapa.ehAtual ? 'rgba(234,88,12,0.35)' : 'rgba(15,23,42,0.08)',
                  bgcolor: etapa.ehAtual ? 'rgba(255,247,237,0.9)' : 'rgba(255,255,255,0.92)',
                  boxShadow: etapa.ehAtual
                    ? '0 8px 24px rgba(234,88,12,0.12)'
                    : '0 4px 14px rgba(15,23,42,0.04)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  cursor: clickable ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 10px 28px rgba(15,23,42,0.08)',
                  },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                        {etapa.nome}
                      </Typography>
                      {etapa.ehAtual && (
                        <Chip
                          size="small"
                          label="AGORA"
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: '0.65rem',
                            bgcolor: '#ea580c',
                            color: '#fff',
                          }}
                        />
                      )}
                    </Stack>
                    <Chip
                      size="small"
                      label={meta.label}
                      sx={{
                        mt: 0.75,
                        height: 22,
                        fontWeight: 700,
                        color: meta.color,
                        bgcolor: meta.bg,
                      }}
                    />
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }} alignItems="center">
                      {etapa.grupoResponsavel && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PersonOutlineRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {etapa.grupoResponsavel}
                          </Typography>
                        </Stack>
                      )}
                      {(etapa.duracaoDias != null || etapa.prazoDias != null) && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TimerOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {etapa.duracaoDias != null ? `${etapa.duracaoDias}d` : '—'}
                            {etapa.prazoDias != null ? ` / prazo ${etapa.prazoDias}d` : ''}
                          </Typography>
                        </Stack>
                      )}
                      {etapa.iniciadoEm && (
                        <Typography variant="caption" color="text.secondary">
                          Início · {formatDt(etapa.iniciadoEm)}
                        </Typography>
                      )}
                      {etapa.concluidoEm && (
                        <Typography variant="caption" color="text.secondary">
                          Fim · {formatDt(etapa.concluidoEm)}
                        </Typography>
                      )}
                    </Stack>
                    {arquivos.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {arquivos.map((arq) => {
                          const url =
                            arq.url ||
                            arq.arquivoUrl ||
                            arq.arquivoPath ||
                            null;
                          if (!url && !arq.arquivoPath) return null;
                          const label =
                            arq.tipo === 'BUNDLE'
                              ? arq.nome || 'Consolidado'
                              : arq.tipo === 'FINAL'
                                ? 'Documento final'
                                : arq.tipo === 'ASSINADO'
                                  ? arq.nome || 'Assinado'
                                  : arq.nome || 'PDF';
                          return (
                            <Chip
                              key={arq.id || label}
                              size="small"
                              icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                              label={label}
                              onClick={() => onArquivoClick?.(arq)}
                              sx={{
                                maxWidth: 220,
                                bgcolor: 'rgba(14,165,233,0.08)',
                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                  {eventos.length > 0 && (
                    <Tooltip title={eventsOpen ? 'Ocultar eventos' : 'Ver eventos'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenEvents((prev) => ({ ...prev, [etapa.id]: !prev[etapa.id] }));
                        }}
                      >
                        {eventsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                <Collapse in={eventsOpen || (etapa.ehAtual && eventos.length > 0 && eventos.length <= 3)}>
                  {eventos.length > 0 && (
                    <Stack spacing={0.75} sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
                      {eventos.map((ev, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 2,
                            bgcolor: 'rgba(15,23,42,0.03)',
                            border: '1px solid rgba(15,23,42,0.06)',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f766e' }}>
                            {ev.acao || 'EVENTO'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {[ev.usuario, formatDt(ev.createdAt)].filter(Boolean).join(' · ')}
                          </Typography>
                          {ev.observacao && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                              {ev.observacao}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Collapse>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
